// src/pages/admin/AdminAnalytics.jsx — Phase 3.1
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Stack, CircularProgress, Alert, Paper,
  Select, MenuItem, FormControl, InputLabel, Chip, Button,
  Table, TableHead, TableRow, TableCell, TableBody, Tooltip,
  LinearProgress, alpha,
} from "@mui/material";
import DownloadIcon          from "@mui/icons-material/Download";
import BarChartIcon          from "@mui/icons-material/BarChart";
import TrendingDownIcon      from "@mui/icons-material/TrendingDown";
import LabelIcon             from "@mui/icons-material/Label";
import { PieChart, Pie, Cell, Tooltip as RTooltip, Legend, ResponsiveContainer,
         BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import API from "../../api";
import { P } from "../../theme";

const DIFF_LABELS  = { 1:"Facile", 2:"Moyen", 3:"Difficile" };
const DIFF_COLORS  = { 1:"#27ae60", 2:"#d97706", 3:"#c0392b" };
const PIE_COLORS   = ["#27ae60","#d97706","#c0392b","#64748b"];

export default function AdminAnalytics() {
  const [exams,     setExams]     = useState([]);
  const [examId,    setExamId]    = useState("");
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [dlLoading, setDlLoading] = useState(false);

  useEffect(() => {
    document.title = "Analytics — ExamOnline Admin";
    API.get("/exams/?page_size=100").then(r => {
      setExams(Array.isArray(r.data) ? r.data : r.data.items || []);
    }).catch(() => {});
  }, []);

  const load = useCallback(async (id) => {
    if (!id) return;
    setLoading(true); setError(""); setData(null);
    try {
      const r = await API.get(`/attempts/analytics/${id}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur de chargement");
    } finally { setLoading(false); }
  }, []);

  const downloadReport = async () => {
    if (!examId) return;
    setDlLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:8001"}/attempts/report/${examId}/pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a"); a.href = url;
      a.download = `rapport_examen_${examId}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { alert("Impossible de télécharger le rapport."); }
    finally { setDlLoading(false); }
  };

  // ── Données pour les graphiques ──
  const diffData = data ? Object.entries(data.difficulty_distribution || {})
    .filter(([k]) => k !== "null" && k !== "None")
    .map(([k, v]) => ({ name: DIFF_LABELS[Number(k)] || "Autre", value: v, color: DIFF_COLORS[Number(k)] || "#64748b" }))
    : [];

  const tagData = data ? Object.entries(data.tag_distribution || {})
    .sort((a,b) => b[1]-a[1]).slice(0,10)
    .map(([tag, count]) => ({ tag, count }))
    : [];

  return (
    <Box sx={{ maxWidth:1100, mx:"auto" }}>
      {/* Header */}
      <Box sx={{ background:P.navy, color:"white", px:{ xs:3, md:4 }, py:3, borderRadius:"16px", mb:3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography sx={{ fontWeight:800, fontSize:"1.3rem" }}>Analytics par examen</Typography>
            <Typography sx={{ color:"rgba(255,255,255,.6)", fontSize:"0.85rem", mt:.25 }}>
              Taux de réussite, questions difficiles, tags et rapport PDF
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <FormControl size="small" sx={{ minWidth:240 }}>
              <InputLabel sx={{ color:"rgba(255,255,255,.7)" }}>Sélectionner un examen</InputLabel>
              <Select value={examId} label="Sélectionner un examen"
                onChange={e => { setExamId(e.target.value); load(e.target.value); }}
                sx={{ color:"white", "& .MuiOutlinedInput-notchedOutline":{ borderColor:"rgba(255,255,255,.3)" },
                  "& .MuiSvgIcon-root":{ color:"white" } }}>
                {exams.map(e => (
                  <MenuItem key={e.id} value={e.id}>{e.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {data && (
              <Button variant="contained" startIcon={<DownloadIcon />}
                onClick={downloadReport} disabled={dlLoading}
                sx={{ background:P.red, "&:hover":{ background:"#a93226" },
                  textTransform:"none", fontWeight:700, borderRadius:"8px", whiteSpace:"nowrap" }}>
                {dlLoading ? "Export…" : "Rapport PDF"}
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      {loading && <Box sx={{ display:"flex", justifyContent:"center", py:10 }}><CircularProgress sx={{ color:P.navy }} /></Box>}
      {error   && <Alert severity="error" sx={{ borderRadius:"8px", mb:2 }}>{error}</Alert>}

      {data && (
        <Stack spacing={3}>
          {/* KPI row */}
          <Box sx={{ display:"grid", gridTemplateColumns:{ xs:"1fr 1fr", sm:"repeat(4,1fr)" }, gap:2 }}>
            {[
              { label:"Tentatives totales", value:data.total_attempts,   color:P.navy },
              { label:"Questions",          value:data.total_questions,   color:P.navy },
              { label:"Taux de réussite moy.",
                value: data.avg_success_rate != null ? `${data.avg_success_rate}%` : "—",
                color: data.avg_success_rate >= 70 ? "#27ae60" : data.avg_success_rate >= 50 ? "#d97706" : "#c0392b" },
              { label:"Tags distincts",
                value: Object.keys(data.tag_distribution||{}).length,
                color:P.navy },
            ].map(k => (
              <Paper key={k.label} sx={{ p:2.5, borderRadius:"12px", textAlign:"center",
                border:`1px solid ${P.slate[200]}` }}>
                <Typography sx={{ fontWeight:800, fontSize:"1.6rem", color:k.color }}>{k.value}</Typography>
                <Typography variant="caption" sx={{ color:P.slate[500], fontWeight:600,
                  textTransform:"uppercase", fontSize:"0.68rem" }}>{k.label}</Typography>
              </Paper>
            ))}
          </Box>

          {/* Graphiques */}
          <Box sx={{ display:"grid", gridTemplateColumns:{ xs:"1fr", md:"1fr 1fr" }, gap:3 }}>
            {/* Donut difficulté */}
            {diffData.length > 0 && (
              <Paper sx={{ p:3, borderRadius:"12px", border:`1px solid ${P.slate[200]}` }}>
                <Typography sx={{ fontWeight:700, color:P.navy, mb:2 }}>Répartition par difficulté</Typography>
                <Box sx={{ height:200 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={diffData} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                        dataKey="value" label={({ name, percent }) => `${name} ${Math.round(percent*100)}%`}
                        labelLine={false}>
                        {diffData.map((d,i) => <Cell key={i} fill={d.color} />)}
                      </Pie>
                      <RTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            )}

            {/* Bar tags */}
            {tagData.length > 0 && (
              <Paper sx={{ p:3, borderRadius:"12px", border:`1px solid ${P.slate[200]}` }}>
                <Typography sx={{ fontWeight:700, color:P.navy, mb:2 }}>Top tags</Typography>
                <Box sx={{ height:200 }}>
                  <ResponsiveContainer>
                    <BarChart data={tagData} layout="vertical" margin={{ left:10, right:10 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize:10 }} />
                      <YAxis type="category" dataKey="tag" tick={{ fontSize:10 }} width={80} />
                      <RTooltip />
                      <Bar dataKey="count" fill={P.navy} radius={[0,4,4,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            )}
          </Box>

          {/* Questions les plus ratées */}
          {data.hardest_questions?.length > 0 && (
            <Paper sx={{ borderRadius:"12px", border:`1px solid ${P.slate[200]}`, overflow:"hidden" }}>
              <Box sx={{ px:3, py:2, background:P.slate[50], borderBottom:`1px solid ${P.slate[200]}` }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <TrendingDownIcon sx={{ color:P.red, fontSize:20 }} />
                  <Typography sx={{ fontWeight:700, color:P.navy }}>
                    Questions les plus ratées (Top {data.hardest_questions.length})
                  </Typography>
                </Stack>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background:P.slate[50] }}>
                    {["#","Question","Tags","Difficulté","Taux réussite","Tentatives"].map(h => (
                      <TableCell key={h} sx={{ fontWeight:700, fontSize:"0.75rem",
                        textTransform:"uppercase", color:P.slate[600] }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.hardest_questions.map((q,i) => (
                    <TableRow key={q.question_id} sx={{ "&:hover":{ background:P.slate[50] } }}>
                      <TableCell sx={{ fontWeight:700, color:P.red }}>{i+1}</TableCell>
                      <TableCell sx={{ maxWidth:280 }}>
                        <Typography variant="body2" sx={{ fontSize:"0.82rem", color:P.slate[700] }}>
                          {q.content}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={.5} flexWrap="wrap">
                          {(q.tags||[]).slice(0,3).map(t => (
                            <Chip key={t} label={t} size="small"
                              sx={{ fontSize:"0.68rem", height:18,
                                background:alpha(P.navy,.07), color:P.navy }} />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {q.difficulty ? (
                          <Chip label={DIFF_LABELS[q.difficulty]} size="small"
                            sx={{ fontSize:"0.72rem", height:20,
                              background:alpha(DIFF_COLORS[q.difficulty],.1),
                              color:DIFF_COLORS[q.difficulty], fontWeight:700 }} />
                        ) : "—"}
                      </TableCell>
                      <TableCell>
                        {q.success_rate != null ? (
                          <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
                            <LinearProgress variant="determinate" value={q.success_rate}
                              sx={{ flex:1, height:6, borderRadius:3,
                                "& .MuiLinearProgress-bar":{ background:
                                  q.success_rate >= 70 ? "#27ae60"
                                  : q.success_rate >= 40 ? "#d97706" : "#c0392b" } }} />
                            <Typography variant="caption" sx={{ fontWeight:700, minWidth:30,
                              color: q.success_rate >= 70 ? "#27ae60"
                                : q.success_rate >= 40 ? "#d97706" : "#c0392b" }}>
                              {q.success_rate}%
                            </Typography>
                          </Box>
                        ) : "—"}
                      </TableCell>
                      <TableCell sx={{ color:P.slate[600], fontWeight:600 }}>{q.total}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          {/* Toutes les questions */}
          <Paper sx={{ borderRadius:"12px", border:`1px solid ${P.slate[200]}`, overflow:"hidden" }}>
            <Box sx={{ px:3, py:2, background:P.slate[50], borderBottom:`1px solid ${P.slate[200]}` }}>
              <Typography sx={{ fontWeight:700, color:P.navy }}>
                Toutes les questions ({data.total_questions})
              </Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background:P.slate[50] }}>
                  {["#","Question","Tags","Difficulté","Taux réussite","Tentatives"].map(h => (
                    <TableCell key={h} sx={{ fontWeight:700, fontSize:"0.75rem",
                      textTransform:"uppercase", color:P.slate[600] }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.questions.map((q,i) => (
                  <TableRow key={q.question_id} sx={{ "&:hover":{ background:P.slate[50] } }}>
                    <TableCell sx={{ fontWeight:600, color:P.slate[500] }}>{i+1}</TableCell>
                    <TableCell sx={{ maxWidth:300 }}>
                      <Typography variant="body2" sx={{ fontSize:"0.82rem", color:P.slate[700] }}>
                        {q.content}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={.5} flexWrap="wrap" gap={.25}>
                        {(q.tags||[]).slice(0,3).map(t => (
                          <Chip key={t} label={t} size="small"
                            sx={{ fontSize:"0.65rem", height:17,
                              background:alpha(P.navy,.06), color:P.navy }} />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {q.difficulty ? (
                        <Chip label={DIFF_LABELS[q.difficulty]} size="small"
                          sx={{ fontSize:"0.7rem", height:18,
                            background:alpha(DIFF_COLORS[q.difficulty],.1),
                            color:DIFF_COLORS[q.difficulty], fontWeight:700 }} />
                      ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                    </TableCell>
                    <TableCell sx={{ minWidth:100 }}>
                      {q.success_rate != null ? (
                        <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
                          <LinearProgress variant="determinate" value={q.success_rate}
                            sx={{ flex:1, height:5, borderRadius:3,
                              "& .MuiLinearProgress-bar":{ background:
                                q.success_rate >= 70 ? "#27ae60"
                                : q.success_rate >= 40 ? "#d97706" : "#c0392b" } }} />
                          <Typography variant="caption" sx={{ fontWeight:700, minWidth:30 }}>
                            {q.success_rate}%
                          </Typography>
                        </Box>
                      ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                    </TableCell>
                    <TableCell sx={{ color:P.slate[500] }}>{q.total || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Stack>
      )}
    </Box>
  );
}
